import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { DataSourceValue } from "abweb/dto/ds/DataSourceValue";
import { DataSourceState } from "abweb/dto/generated/ds/DataSourceState";
import { DataSourceValueVectorBase } from "abweb/dto/generated/ds/DataSourceValueVectorBase";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { TSOGUtils } from "abweb/utils/json/TSOGUtils";
import "@abinitio/kendo.all";
export class DataSourceValueVector extends DataSourceValueVectorBase {
  /**
   * Simplified factory method for creating a DataSourceValueVector in a NORMAL state with no baseline.
   * @param id
   * @param value
   * @param refID
   * @returns {DataSourceReference}
   */
  static createWithReadOnlyDefaults(id, items) {
    return DataSourceValueVector.createDataSourceValueVector(/*attributeValues*/null, /*clientTag*/null, /*dataSourceSaveAction*/null, id, items, /*state*/DataSourceState.NORMAL, /*editableOverwrite*/null, /*fieldTypeOverwrite*/null);
  }
  static createDataSourceValueVector(attributeValues, clientTag, dataSourceSaveAction, id, items, state = DataSourceState.NORMAL, editableOverwrite = null, itemFieldTypeOverwrite = null) {
    const retVal = new DataSourceValueVector();
    retVal.initDataSourceValueVector(attributeValues, clientTag, dataSourceSaveAction, editableOverwrite, id, state, itemFieldTypeOverwrite, items);
    for (const item of items) {
      item._containerRecord = retVal;
    }
    retVal.initArray();
    return retVal;
  }
  constructor() {
    super();
    _defineProperty(this, "observableArray", null);
  }
  get refID() {
    // Overridden in DataSourceBaseValue
    return null;
  }
  set refID(value) {
    // Overridden in DataSourceBaseValue
    throw new Error("DataSourceValue does not support refId, much less setting it.");
  }
  initArray() {
    this.observableArray = new kendo.data.ObservableArray(this._items);
    this.observableArray.bind("change", ev1 => this.arrayChangeHandler(ev1));
  }
  get itemFieldType() {
    if (this.itemFieldTypeOverwrite) {
      return this.itemFieldTypeOverwrite;
    }
    if (this._field && this._field.itemType) {
      return this._field.itemType.fieldType;
    }
    return null;
  }
  arrayChangeHandler(event) {
    this.trigger("change", event); // pass the original change to any DSVV listeners

    if (event.action === "add") {
      // Make sure _containerRecord gets set
      for (const item of event.items) {
        item._containerRecord = this;
      }
    }
    this.onCurrentValueChanged();
  }
  onCurrentValueChanged() {
    this.updateLocalStateValue();
    if (this._dataSource) {
      this._dataSource.onContainedValueChanged(this);
    }
  }
  updateLocalStateValue() {
    // Note that do not use the helper isAddedLocal || isDeletedLocal because we only want to consider MY localState
    // and not check _containerRecord.localState
    if (this.localState === DataSourceState.ADD || this.localState === DataSourceState.DELETE) {
      return;
    }

    // Need to walk items and look for changes
    let newValue = DataSourceState.NORMAL;
    for (const item of this.items) {
      if (item.hasLocalChange || item.isAddedLocal || item.isDeletedLocal) {
        newValue = DataSourceState.CHANGE;
        break;
      }
    }
    if (newValue === this.localState) {
      // No change
      return;
    }

    // We have a change
    this.set("localState", newValue);
  }

  /**
   * Gets the current value part of this DataSourceValue.
   * Initially, the currentValue is the same as value, but if the client
   * makes local changes, the currentValue represents those local changes.
   * This getter MUST be overridden by subclasses to return the value field.
   */
  getCurrentValue() {
    return this.items;
  }

  /**
   * Gets the initial value part of this DataSourceValue.
   * This getter MUST be overridden by subclasses to return the value field.
   */
  getValue() {
    return this.items;
  }

  /**
   * Gets the current baseline part of this DataSourceValue.
   * This getter MUST be overridden by subclasses to return the value field.
   */
  getBaseline() {
    return null;
  }
  get isVector() {
    return true;
  }
  isEmpty() {
    return this.items.length === 0;
  }
  updateValidationError() {
    // Do nothing here. Not sure that vectors can be invalid???
    // Maybe their items can, particularly if editing a vector of subrecords, but since I don't know what
    // that means yet, too bad.
  }
  prepareValueForSave() {
    super.prepareValueForSave();

    // Process items
    for (const item of this.items) {
      item.prepareValueForSave();
    }
  }
  getDisplayValue() {
    // Not sure what we should do here
    return `[Vector[${this.items.length}]]`;
  }
  get items() {
    return this.observableArray;
  }
  set items(value) {
    this._items = value;
    this.initArray();
  }
  initFromJson(json, deserializeContext) {
    super.initFromJson(json, deserializeContext);
    this.initArray();
  }
  toTSOG(result, serializeContext) {
    // hide away the _items to keep them from appearing in the serialization.
    const savedItems = this._items;
    this._items = null;
    const retVal = super.toTSOG(result, serializeContext);
    if (this.items != null) {
      retVal["items"] = TSOGUtils.toTSOGArray(this.items, serializeContext);
    }

    // put the _items back
    this._items = savedItems;
    return retVal;
  }

  /**
   * Marks an item as deleted.
   * If the localState of the item is ADD, the item itself is removed,
   * otherwise the localState of the item is simply marked as DELETE.
   * @param item Item in the items list to delete.
   */
  deleteItem(item) {
    const index = this.items.indexOf(item);
    if (index < 0) {
      return;
    }
    this.deleteItemAt(index);
  }

  /**
   * Marks an item as deleted.
   * If the localState of the item is ADD, the item itself is removed,
   * otherwise the localState of the item is simply marked as DELETE.
   * @param index Zero-based index of the item to delete.
   */
  deleteItemAt(index) {
    const item = this.items[index];
    if (item.isDeletedLocal) {
      // Already there
      return;
    }
    if (item.isDeleted) {
      // Item is marked for DELETE on server already
      // so special care is needed
      item.markForDelete();

      // Note that we're not setting localState = CHANGE here because this change may bring us back to NORMAL
      // but we need to check all of our items
      this.updateLocalState();
    } else if (item.isAddedLocal) {
      // We can just remove this item
      this.items.splice(index, 1);
      this.updateLocalState();
    } else {
      item.markForDelete();
      if (this.isAddedLocal || this.isDeletedLocal) {
        return;
      }
      this.set("localState", DataSourceState.CHANGE);
    }
  }
  undeleteItem(item) {
    const index = this.items.indexOf(item);
    if (index < 0) {
      return;
    }
    this.undeleteItemAt(index);
  }
  undeleteItemAt(index) {
    const item = this.items[index];
    if (item.isDeleted) {
      // Item is marked for DELETE on server already
      // so special care is needed
      // In this case markForDelete(true) will set the item.localState = ADD
      item.markForDelete(true);
      this.set("localState", DataSourceState.CHANGE);
      return;
    }
    if (!item.isDeletedLocal) {
      // Nothing to do
      return;
    }
    item.markForDelete(true);
    this.updateLocalState();
  }
  updateLocalState() {
    this.updateLocalStateValue();
  }
  revertLocalChange() {
    if (!this.hasLocalChange) {
      return false;
    }
    for (let n = this.items.length - 1; n >= 0; n--) {
      const item = this.items[n];
      if (item.isAddedLocal) {
        if (item.isDeleted) {
          // Case where item is re-added
          item.markForDelete();
        } else {
          // Remove item
          this.items.splice(n, 1);
        }
      } else if (item.isDeletedLocal) {
        item.markForDelete(true);
        item.revertLocalChange();
      } else if (item.isReAddedLocal) {
        item.markForDelete(false);
        item.revertLocalChange();
      } else {
        item.revertLocalChange();
      }
    }
    this.set("localState", DataSourceState.NORMAL);
    return true;
  }
  coerceValue(value) {
    // No implementation for vector yet
    // TODO: We can probably make this work as a copy from another Vector, though
    // Not sure what to do, but this won't hurt anybody... hopefully.
    return value;
  }
  findItemById(id) {
    for (const item of this.items) {
      if (item.id === id) {
        return item;
      }
    }
    return null;
  }
  findIndexById(id) {
    for (let n = 0; n < this.items.length; n++) {
      if (this.items[n].id === id) {
        return n;
      }
    }
    return -1;
  }
  findIndexByItem(item) {
    for (let n = 0; n < this.items.length; n++) {
      if (this.items[n] === item) {
        return n;
      }
    }
    return -1;
  }
  addItem(item) {
    const field = this._field == null ? null : this._field.itemType;
    item._field = field;
    item._containerRecord = this;
    item.localState = DataSourceState.ADD;
    if (this.observableArray) {
      this.observableArray.push(item);
    }
  }
  removeItem(arg) {
    let index = -1;
    if (typeof arg === "number") {
      index = arg;
      if (index < 0 || index >= this.items.length) {
        index = -1;
      }
    } else if (typeof arg === "string") {
      // Find index by id
      index = this.findIndexById(arg);
    } else if (ClassUtils.isInstanceOf(arg, DataSourceValue)) {
      index = this.findIndexByItem(arg);
    }
    if (index < 0) {
      return null;
    }
    const item = this.items[index];

    // Figure out how to deal with this
    if (item.isAddedLocal) {
      // We can remove this item
      this.items.splice(index, 1);
    } else {
      // Mark it as removed
      item.set("localState", DataSourceState.DELETE);
    }
    return item;
  }
}
//# sourceMappingURL=DataSourceValueVector.js.map
